<template>
  <div class="teacher-info">
    <back-to-list
      :label="$t($i18nLabelsHelper(currentOrganizationType, 'all_teachers'))"
      :backTo="backToTeachers"
    />
    <el-row type="flex" justify="center" class="heading">
      <el-col :md="20" :xs="24">
        <h1>{{ fullName }}</h1>
      </el-col>
    </el-row>
    <el-row type="flex" :gutter="32" justify="center" class="details">
      <el-col :md="6" :xs="24">
        <elsa-section :title="$t('teacher_info.profile')">
          <template v-slot:body>
            <initial-avatar v-bind="$attrs" :avatarUrl="currentTeacher.photo_url" size="large" :userObject=currentTeacher />
            <info-row
              :label="$t('teacher_info.first_name')"
              :value="currentTeacher.first_name"
            />
            <info-row
              :label="$t('teacher_info.last_name')"
              :value="currentTeacher.last_name"
            />
            <info-row
              :label="$t('teacher_info.email')"
              :value="currentTeacher.email"
            />
            <info-row
              :label="$t('teacher_info.username')"
              :value="currentTeacher.username"
            />
            <info-row
              :label="$t('teacher_info.phone_number')"
              :value="currentTeacher.phone_number || ''"
            />
          </template>
        </elsa-section>
        <elsa-section :title="$t('teacher_info.user_roles')" class="spacer">
          <template v-slot:body>
            <info-row
              :label="$t('teacher_info.department')"
              :value="currentTeacher.department || ''"
            />
            <info-row
              :label="$t('teacher_info.title')"
              :value="currentTeacher.title || ''"
            />
            <info-row :label="$t('teacher_info.user_role')">
              <role v-for="(role, idx) in getTeacherRoles" :key="idx" :label="role" />
            </info-row>
          </template>
        </elsa-section>
        <div class="delete">
          <i class="elsa-icon-grey-delete" />
          {{ $t($i18nLabelsHelper(currentOrganizationType, 'remove_teacher_from_organization')) }}
        </div>
      </el-col>
      <el-col :md="14" :xs="24">
        <elsa-section class="overview" :title="$t('teacher_info.overview')">
          <template v-slot:body>
            <icon-info-box
              icon="class-white"
              :value="currentTeacher.groups_count"
              :label="$t($i18nLabelsHelper(currentOrganizationType, 'groups'))"
              :showListText="$t($i18nLabelsHelper(currentOrganizationType, 'teacher_info.show_list_of_classes'))"
              :showListAction="showClassList"
            />
            <icon-info-box
              icon="students-class"
              :value="currentTeacher.users_groups_count"
              :label="$t($i18nLabelsHelper(currentOrganizationType, 'students'))"
              :showListText="$t($i18nLabelsHelper(currentOrganizationType, 'teacher_info.show_list_of_students'))"
              :showListAction="showStudentList"
            />
          </template>
        </elsa-section>
        <elsa-section
          :title="`${$t($i18nLabelsHelper(currentOrganizationType, 'teacher_info.classes_assigned_to'))} (${currentTeacher.groups_count})`"
          class="spacer"
        >
          <template v-slot:body>
            <div class="class-element" v-for="(element, idx) in classes.slice(0,5)" :key="idx">
              <h3> {{ element.name }} </h3>
              <class-progress
                :classData="element"
              />
            </div>
            <div v-if="currentTeacher.groups_count > 0" class="show-list" @click="showClassList">{{ $t('teacher_info.show_all_classes') }}</div>
          </template>
        </elsa-section>
      </el-col>
    </el-row>
  </div>
</template>

<script>
  import BackToList from '@/components/common/BackToList'
  import ElsaSection from '@/components/common/ElsaSection'
  import InfoRow from '@/components/common/InfoRow'
  import Role from '@/components/common/Role'
  import IconInfoBox from '@/components/common/IconInfoBox'
  import ClassProgress from '@/components/common/ClassProgress'
  import InitialAvatar from '@/components/common/InitialAvatar'
  import { mapGetters, mapActions } from 'vuex'
  import { uniq } from 'ramda';

  export default {
    components: {
      BackToList,
      ElsaSection,
      InfoRow,
      Role,
      IconInfoBox,
      ClassProgress,
      InitialAvatar
    },
    props: {
      teacher: {
        type: Object,
        default: () => {}
      },
      organizationId: {},
      teacherId: {},
    },
    data() {
      return {
        teachersClass: []
      }
    },
    computed: {
     ...mapGetters({
        currentOrganizationType: 'organization/currentOrganizationType',
        currentOrganizationId: 'organization/currentOrganizationId',
        currentTeacher: 'teachers/currentTeacher',
        classes: 'classes/getClassList',
      }),
      fullName() {
        return `${this.currentTeacher.first_name} ${this.currentTeacher.last_name}`
      },
      
      getTeacherRoles() {
        return this.currentTeacher.roles ? uniq(this.currentTeacher.roles) : []
      }
    },
    methods: {
      ...mapActions({
        getClasses: 'classes/getClasses'
      }),
      backToTeachers() {
        this.$router.push({
           name: 'teachers',
           params: {
             organizationId: this.organizationId,
           }
        })
      },
      showClassList() {
        this.$router.push({
          name: 'classes',
          params: {
            organizationId: this.organizationId,
            owner: this.currentTeacher.id
          }
        })
      },
      showStudentList(){
        this.$router.push({
          name: 'students',
          params: {
            organizationId: this.organizationId,
            owner: this.currentTeacher.id
          }
        })
      }
    },
    async mounted () {
      try {
        await this.$store.dispatch('teachers/getTeacher', {
          organizationId: this.organizationId,
          teacherId: this.teacherId
        })
        this.getClasses({ owner: this.currentTeacher.id })
      } catch(error) {
        error
      }
    },
  }
</script>

<style lang="scss" scoped>
  .heading {
    margin-top: 52px;
  }

  .teacher-info {
    padding: 24px 32px;
  }

  .details {
    margin-top: 25px;
  }

  h1 {
    border: none;
  }

  .info-row {
    margin-bottom: 26px;
    &:last-of-type {
      margin-bottom: 0;
    }
  }

  .elsa-section {
     padding-bottom: 32px;
     &::v-deep .section-body {
       margin-top: 24px;
     }
  }

  img {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 26px;
  }

  ::v-deep .overview .section-body {
    display: flex;
    align-items: center;
    .icon-info-box {
      width: 50%;
      &:not(:last-of-type) {
        margin-bottom: 0;
      }
    }
  }

  ::v-deep .class-status.hidden {
    display: none;
  }

  .show-list {
    margin-top: 48px;
    display: inline-block;
  }

  .class-element {
    &:not(:first-of-type) {
      margin-top: 48px;
      }
  }

  .delete {
    display: flex;
    font-weight: 600;
    font-size: 14px;
    text-transform: capitalize;
    color: #546873;
    line-height: 48px;
    margin-top: 32px;
    cursor: pointer;
    i {
      margin-right: 15px;
      &:after {
        margin-left: 0;
      }
    }
  }

</style>
